/**
 * Moon Component
 * Main orchestrator component for rendering the Moon
 */

import React, { useState, useMemo, useEffect, useRef, memo } from 'react'
import { useFrame, useThree } from '@react-three/fiber'
import { Vector3 } from 'three'
import MoonSphere from './MoonSphere'
import lodConfig from './config/lodConfig.json'
import { useMaterialCache } from './hooks/useMaterialCache'
import { useSceneReady } from '../SceneReadyManager'
import { getDeviceInfo } from '../../utils/deviceDetection'

export function Moon({
  radius = 0.273, // Moon is about 27.3% the radius of Earth
  position = [0, 0, 0],
  rotation = [0, 0, 0],
  autoRotate = false,
  rotationSpeed = 0.004, // Moon rotates much slower (27.3 days)
  textureQuality = 'auto',
  onLoad = null,
  onLODUpdate = null,
  preloadComplete = false,
  isOrbiting = false, // Whether the moon is orbiting and rotating
  forceLOD = null, // Override quality level e.g. 'LOD_1' when focused on a location
}) {
  const [isLoaded, setIsLoaded] = useState(false)
  const { camera } = useThree()

  // Detect device and orientation (memoized)
  const deviceInfo = useMemo(() => getDeviceInfo(), [])
  const isMobile = deviceInfo.isMobile
  
  // Get appropriate LOD config for device and filter out disabled LODs (memoized)
  const lodLevels = useMemo(() => {
    const allLods = isMobile ? lodConfig.mobile.lodLevels : lodConfig.desktop.lodLevels
    return allLods.filter(lod => lod.enabled !== false) // Default to enabled if not specified
  }, [isMobile])

  // Pre-create all materials for instant LOD switching
  const { materialCache, isReady: materialsReady } = useMaterialCache(lodLevels, preloadComplete)

  // FIXED LOD - No dynamic switching (memory leak prevention)
  const fixedQuality = 'LOD_3'
  const currentQuality = forceLOD || fixedQuality

  // Auto-rotation - use ref to avoid state updates every frame
  const spinRotationRef = useRef(0)
  const moonGroupRef = useRef()
  
  // Reusable Vector3 to avoid allocations every frame
  const moonPositionRef = useRef(new Vector3())
  
  // Reusable LOD update object to prevent recreation
  const lodUpdateRef = useRef({
    quality: fixedQuality,
    isMobile
  })

  useFrame((state, delta) => {
    if (autoRotate && isOrbiting) {
      // Update ref directly instead of triggering state update
      spinRotationRef.current += rotationSpeed * delta
      
      // Apply rotation directly to the group
      if (moonGroupRef.current) {
        const innerGroup = moonGroupRef.current.children[0]
        if (innerGroup) {
          innerGroup.rotation.y = spinRotationRef.current
        }
      }
    }

    // LOD SYSTEM DISABLED - Fixed to LOD_3. onLODUpdate removed (no consumer).
  })

  // Callback when loaded
  const handleLoad = () => {
    if (!isLoaded) {
      setIsLoaded(true)
      if (onLoad) {
        onLoad()
      }
    }
  }

  // Track when Moon is fully ready (materials loaded + rendered)
  const [isRendered, setIsRendered] = useState(false)
  
  useEffect(() => {
    if (materialsReady && !isRendered) {
      // Wait one frame to ensure geometry is rendered
      const timer = setTimeout(() => {
        setIsRendered(true)
      }, 100)
      return () => clearTimeout(timer)
    }
  }, [materialsReady, isRendered])
  
  // Report ready state to Scene Manager
  useSceneReady('moon', isRendered)

  // Don't render until materials are ready
  if (!materialsReady) {
    return null
  }

  return (
    <group ref={moonGroupRef} position={position} rotation={rotation}>
      <group>
        <MoonSphere
          radius={radius}
          rotation={[0, 0, 0]}
          quality={currentQuality}
          position={[0, 0, 0]}
          materialCache={materialCache}
        />
      </group>
    </group>
  )
}

export default memo(Moon)
